import React from 'react';
import { colors, StapleTableCell, StapleTableRow } from '@insurancemenu/imenu-components-lib';
import { CheckCircle, RadioButtonChecked, RadioButtonUnchecked } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useHistory } from 'react-router-dom';
import { celticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import {
  getProductLinesFromArray,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { FormInstanceType, FormStatuses } from 'types/formService';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { useEmployerFormsPageStore } from 'store/pages/enrollment/employer-forms/pageStore';
import { ActionButtonStyled, CardStatusTextStyled, StatusLabelStyled } from './styles';
import { WEB_FORM_ACTION_BUTTON_TEST_ID } from '../constants';

interface Props {
  formInstance: FormInstanceType;
}

interface StatusLabelProps {
  status: FormStatuses;
}

interface ActionButtonProps {
  status: FormStatuses;
  formInstance: FormInstanceType;
}

const formInNotAvailableByRole = (formInstance: FormInstanceType, roles: SupportedUserRoles[]): boolean => {
  if (celticsRights(roles)) {
    return false;
  }
  if (!formInstance.form.roleAvailability?.length) {
    return false;
  }
  return (
    formInstance.form.roleAvailability.length > 0 &&
    !formInstance.form.roleAvailability.some((element) => {
      return roles.includes(element);
    })
  );
};

const StatusLabel: React.FC<StatusLabelProps> = ({ status }) => {
  switch (status) {
    case FormStatuses.COMPLETE:
      return (
        <StatusLabelStyled $status={status}>
          <CheckCircle width="2rem" color={colors.green[600]} />
          <CardStatusTextStyled>Completed</CardStatusTextStyled>
        </StatusLabelStyled>
      );
    case FormStatuses.STARTED:
      return (
        <StatusLabelStyled $status={status}>
          <RadioButtonChecked width="2rem" color={colors.orange[600]} />
          <CardStatusTextStyled>In Progress</CardStatusTextStyled>
        </StatusLabelStyled>
      );
    default:
      return (
        <StatusLabelStyled $status={status}>
          <RadioButtonUnchecked width="2rem" color={colors.blue[600]} />
          <CardStatusTextStyled>Incomplete</CardStatusTextStyled>
        </StatusLabelStyled>
      );
  }
};

const ActionButton: React.FC<ActionButtonProps> = observer(({ status, formInstance }) => {
  const history = useHistory();
  const {
    userStore: { userRoles },
  } = useRootStore();
  const pageStore = useEmployerFormsPageStore();
  const periodId = pageStore.domainData.periodId;

  const handleClick = (): void => {
    history.push(`/web-form/${periodId}/${formInstance.id}`);
  };

  if (formInNotAvailableByRole(formInstance, userRoles)) {
    return (
      <ActionButtonStyled onClick={handleClick} size="medium" disabled data-test-id={WEB_FORM_ACTION_BUTTON_TEST_ID}>
        Unavailable
      </ActionButtonStyled>
    );
  }

  if (status === FormStatuses.COMPLETE) {
    return (
      <ActionButtonStyled
        onClick={handleClick}
        size="medium"
        type="opaque"
        color="primary"
        data-test-id={WEB_FORM_ACTION_BUTTON_TEST_ID}
      >
        View / Edit
      </ActionButtonStyled>
    );
  }

  return (
    <ActionButtonStyled
      onClick={handleClick}
      size="medium"
      color="primary"
      data-test-id={WEB_FORM_ACTION_BUTTON_TEST_ID}
    >
      Complete Form
    </ActionButtonStyled>
  );
});

const EmployerFormTableRow: React.FC<Props> = observer((props) => {
  return (
    <StapleTableRow>
      <StapleTableCell>{props.formInstance.form.formTitle}</StapleTableCell>
      <StapleTableCell>
        {getProductLinesFromArray(props.formInstance.productLineIds.slice().sort(sortByProductLineId))}
      </StapleTableCell>
      <StapleTableCell>
        <StatusLabel status={props.formInstance.status} />
      </StapleTableCell>
      <StapleTableCell>
        <ActionButton status={props.formInstance.status} formInstance={props.formInstance} />
      </StapleTableCell>
    </StapleTableRow>
  );
});

export default EmployerFormTableRow;
